import * as React from "react";
import { Box, Button, Card, ListItem, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { SMB_PLATFORM } from "@/runtime-env";
import { goTo, IN_SMB } from "@/lib/platform";
import { Account, useAccountsList, useImpersonateAccount } from "@/hooks/api/use-accounts";
import { fetchOnboardingState, isOnboardingIncomplete, onboardingKeys } from "@/hooks/api/use-onboarding";

import { useAuth } from "../core/auth-provider";
import { RouterLink } from "../core/link";
import { DynamicLogo } from "../core/logo";
import { toast } from "../core/toaster";
import { CustomSkeleton } from "../customSkeleton/custom-skeleton";

export function AccountPicker(): React.JSX.Element {
	const { user, setAccount, getMe, startUrl } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [impersonateId, setImpersonateId] = React.useState<string | null>(null);

	const impersonateAccountMutation = useImpersonateAccount();

	const { data: accountsData, isLoading } = useAccountsList();
	const accounts = accountsData || [];

	const handleImpersonate = async (selectedAccount: Account) => {
		try {
			setImpersonateId(selectedAccount.id);
			const response = await impersonateAccountMutation.mutateAsync(selectedAccount.id);
			toast.success("Account selected successfully!");
			setAccount(response.account);
			await getMe();

			// Super admins always land in the enterprise app, even for SMB-typed
			// accounts (platform workspace, support access).
			const isSmb =
				SMB_PLATFORM && (response.account?.type ?? selectedAccount.type) === "smb" && !user?.isSuperAdmin;
			if (isSmb) {
				// The previous account's setup state must not decide where this one lands.
				queryClient.removeQueries({ queryKey: onboardingKeys.all });
				const onboarding = await fetchOnboardingState(queryClient);
				goTo(navigate, "smb", isOnboardingIncomplete(onboarding) ? "/onboarding" : "/", { replace: true });
			} else {
				goTo(navigate, "admin", IN_SMB ? "/" : startUrl, { replace: true });
			}
		} catch (error) {
			console.error("Failed to select account:", error);
		} finally {
			setImpersonateId(null);
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				p: 4,
				backgroundColor: "background.default",
			}}
		>
			<Stack spacing={4} sx={{ width: "100%", maxWidth: 500 }}>
				<Box sx={{ textAlign: "center" }}>
					<Box component={RouterLink} href={"/sign-in"} sx={{ display: "flex", fontSize: 0, justifyContent: "center" }}>
						<DynamicLogo colorDark="light" colorLight="dark" height={42} width={222} />
					</Box>
					{(accountsData?.length > 0 || isLoading) && (
						<>
							<Typography variant="h4" sx={{ mt: 3, fontWeight: 600 }}>
								Select Account
							</Typography>
							<Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
								Choose an account to continue
							</Typography>
						</>
					)}
				</Box>

				{isLoading ? (
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<CustomSkeleton variant="rounded" width={"100%"} height={64} />
					</Box>
				) : accounts.length > 0 ? (
					<Card
						sx={{
							bgcolor: "background.paper",
							borderRadius: 2,
							boxShadow: 1,
							maxHeight: "calc(100dvh - 300px)",
						}}
					>
						<Box
							sx={{
								overflowY: "auto",
								maxHeight: "inherit",
								pr: 0,
							}}
						>
							{accounts.map((accountItem, index) => (
								<ListItem
									key={accountItem.id}
									disablePadding
									sx={{
										borderBottom: index < accounts.length - 1 ? "1px solid" : "none",
										borderColor: "divider",
									}}
								>
									<ListItemButton
										onClick={() => handleImpersonate(accountItem)}
										disabled={impersonateId === accountItem.id}
										sx={{
											py: 2,
											px: 3,
											"&:hover": {
												backgroundColor: "action.hover",
											},
										}}
									>
										<ListItemText
											primary={accountItem.name}
											primaryTypographyProps={{
												variant: "h6",
												fontWeight: 500,
											}}
										/>
										<Button
											variant="contained"
											size="small"
											disabled={impersonateId === accountItem.id}
											onClick={(e) => {
												e.stopPropagation();
												handleImpersonate(accountItem);
											}}
											loading={impersonateId === accountItem.id}
										>
											{"Select"}
										</Button>
									</ListItemButton>
								</ListItem>
							))}
						</Box>
					</Card>
				) : (
					<Box sx={{ textAlign: "center", mt: 0 }}>
						<Typography variant="h4" sx={{ fontWeight: 600 }}>
							No accounts available
						</Typography>
						<Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
							Contact your administrator to get access to accounts.
						</Typography>
					</Box>
				)}
			</Stack>
			<Button
				variant="contained"
				color="primary"
				onClick={() => navigate("/sign-in")}
				sx={{ width: "max-content", mt: 4 }}
				startIcon={<ArrowLeftIcon />}
			>
				Back to sign in
			</Button>
		</Box>
	);
}
